import { announceLive } from '../lib/live';
import { timeAgo, type AppNotification, type NotificationList, t } from '@procumi/shared';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import type { Route } from '../routes';
import { Avatar } from './Avatar';

/**
 * Clopoțelul (cerut de proprietar, 2026-10-10): numărul celor necitite și,
 * la clic, lista. Notificările noi vin pe loc, pe conexiunea deschisă de
 * server (`/api/notifications/stream`); dacă ea cade, întreabă la 30 de
 * secunde. Fiecare primește doar ce îl privește (vezi api/notifications.ts).
 */
export function Bell({ go }: { go: (r: Route) => void }) {
  const [list, setList] = useState<NotificationList>({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const load = useCallback(() => {
    api
      .notifications()
      .then(setList)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    let poll: number | undefined;
    const es = new EventSource('/api/notifications/stream');
    es.addEventListener('notification', (ev) => {
      const n = JSON.parse((ev as MessageEvent).data) as AppNotification;
      announceLive();
      setList((l) =>
        l.items.some((x) => x.id === n.id)
          ? l
          : { items: [n, ...l.items].slice(0, 30), unread: l.unread + 1 },
      );
    });
    es.onerror = () => {
      // Conexiunea a căzut (server repornit, rețea): întrebăm singuri până revine.
      if (poll === undefined) poll = window.setInterval(load, 30_000);
    };
    es.onopen = () => {
      if (poll !== undefined) {
        window.clearInterval(poll);
        poll = undefined;
        load();
      }
    };
    return () => {
      es.close();
      if (poll !== undefined) window.clearInterval(poll);
    };
  }, [load]);

  // Lista stă deasupra paginii, lângă clopoțel (bara laterală își taie conținutul).
  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const width = Math.min(380, window.innerWidth - 16);
    setPos({ top: r.bottom + 8, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!pop.current?.contains(t) && !btn.current?.contains(t)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const markRead = async (ids?: string[]) => {
    setList((l) => ({
      items: l.items.map((n) => (!ids || ids.includes(n.id) ? { ...n, read: true } : n)),
      unread: ids ? Math.max(0, l.unread - l.items.filter((n) => !n.read && ids.includes(n.id)).length) : 0,
    }));
    try {
      const r = await api.readNotifications(ids);
      setList((l) => ({ ...l, unread: r.unread }));
    } catch {
      load();
    }
  };

  const openItem = (n: AppNotification) => {
    if (!n.read) void markRead([n.id]);
    setOpen(false);
    // agentul: oferta citită se deschide pe cerere; mailul nepotrivit, în căsuță
    if (n.kind === 'inbox_unmatched') go({ view: 'agent' });
    else if (n.kind === 'inbox_ready') go(n.rfqId ? { view: 'rfq', id: n.rfqId } : { view: 'agent' });
    else if (n.orderId && n.rfqId) go({ view: 'order', rfqId: n.rfqId, orderId: n.orderId });
    else if (n.rfqId) go({ view: 'rfq', id: n.rfqId });
    else if (n.requisitionId) go({ view: 'requisition', id: n.requisitionId });
  };

  const now = Date.now();
  return (
    <>
      <button
        ref={btn}
        type="button"
        className="bell"
        aria-label={list.unread ? t('Notificări, {0} necitite', list.unread) : t('Notificări')}
        aria-expanded={open}
        onClick={() => {
          if (!open) load();
          setOpen(!open);
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path
            d="M9 2.2a4.6 4.6 0 0 0-4.6 4.6v2.7L3 12.4h12l-1.4-2.9V6.8A4.6 4.6 0 0 0 9 2.2ZM7.2 14.6a1.9 1.9 0 0 0 3.6 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
        {list.unread ? <span className="bell-n">{list.unread > 99 ? '99+' : list.unread}</span> : null}
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={pop}
              className="bell-pop"
              role="dialog"
              aria-label={t('Notificări')}
              style={{ top: pos.top, left: pos.left }}
            >
              <header>
                <b>{t('Notificări')}</b>
                {list.unread ? <span className="bell-c">{list.unread}</span> : null}
                {list.unread ? (
                  <button className="link" onClick={() => void markRead()}>
                    {t('Marchează toate ca citite')}
                  </button>
                ) : null}
              </header>
              {list.items.length ? (
                <ul>
                  {list.items.map((n) => (
                    <li key={n.id}>
                      <button className={`bell-row${n.read ? '' : ' new'}`} onClick={() => openItem(n)}>
                        {n.actorName ? (
                          <Avatar name={n.actorName} userId={n.actorId} avatarAt={n.actorAvatarAt} />
                        ) : (
                          <span className="av none" aria-hidden="true" />
                        )}
                        <span className="bell-t">
                          <span>
                            {n.actorName ? <b>{n.actorName}</b> : null}
                            {n.actorName ? ' · ' : ''}
                            {t(n.text)}
                          </span>
                          <small>{timeAgo(n.createdAt, now)}</small>
                        </span>
                        {n.read ? null : <span className="bell-dot" aria-label={t('necitită')} />}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="bell-empty">
                  {t(
                    'Nimic nou. Aici apar comenzile de aprobat, ofertele noi, comentariile și necesarele care te privesc.',
                  )}
                </p>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
